import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, ArrowLeft, Loader2, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Logo } from "@/components/ui/Logo";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast({
        variant: "destructive",
        title: "Email Required",
        description: "Please enter your email address.",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setSent(true);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-secondary">
      <Card className="w-full max-w-md border-2 shadow-lg">
        <CardHeader className="text-center p-4 sm:p-6">
          <Link to="/" className="flex items-center justify-center mb-4">
            <Logo size="md" />
          </Link>
          {sent ? (
            <>
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-green-500/10">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <CardTitle className="text-xl sm:text-2xl">Check your email</CardTitle>
              <CardDescription className="text-sm">
                We've sent a reset link to <strong>{email}</strong>
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-xl sm:text-2xl">Forgot password?</CardTitle>
              <CardDescription className="text-sm">
                Enter your email and we'll send you a reset link
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          {sent ? (
            <div className="space-y-4">
              <p className="text-xs sm:text-sm text-muted-foreground text-center">
                Didn't receive it? Check spam or try again.
              </p>
              <Button
                variant="outline"
                className="w-full h-11"
                onClick={() => {
                  setSent(false);
                  setEmail("");
                }}
              >
                Try again
              </Button>
              <Link to="/auth" className="block">
                <Button variant="ghost" className="w-full h-11">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to sign in
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    className="pl-9 h-11"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-11" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send reset link
              </Button>

              <Link to="/auth" className="block">
                <Button variant="ghost" className="w-full h-11">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to sign in
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
